<script setup>
import { ref, computed, watch } from 'vue'
import { useUserStore } from '../../stores/userStore'
import axios from '../../axios'

const userStore = useUserStore()
const isAdmin = computed(() => userStore.currentUser?.role === 'admin')

const props = defineProps({
  show: {
    type: Boolean,
    required: true
  },
  fixtureId: {
    type: [String, Number],
    required: true
  },
  currentLeagueId: {
    type: [Number, String],
    default: null
  },
  currentLeagueName: {
    type: String,
    default: ''
  },
  // Fixture V2: pick a league first, then save with an explicit Confirm step.
  confirmChange: {
    type: Boolean,
    default: false
  },
  warning: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['close', 'league-updated'])

const leagues = ref([])
const loading = ref(false)
const saving = ref(false)
const searchQuery = ref('')
const errorMessage = ref('')
const pendingLeague = ref(null)

const filteredLeagues = computed(() => {
  if (!searchQuery.value.trim()) return leagues.value
  const query = searchQuery.value.toLowerCase()
  return leagues.value.filter(l =>
    (l.league_name || l.name || '').toLowerCase().includes(query)
  )
})

async function fetchLeagues() {
  loading.value = true
  errorMessage.value = ''
  try {
    const response = await axios.get('/api/leagues/get-leagues')
    leagues.value = response.data || []
  } catch (err) {
    console.error('Failed to fetch leagues:', err)
    errorMessage.value = 'Failed to load leagues'
    leagues.value = []
  } finally {
    loading.value = false
  }
}

async function selectLeague(league) {
  if (!league || league.id == props.currentLeagueId) {
    emit('close')
    return
  }
  if (props.confirmChange) {
    pendingLeague.value = league
    return
  }
  await saveLeague(league)
}

async function saveLeague(league) {
  if (saving.value) return
  saving.value = true
  errorMessage.value = ''
  try {
    await axios.put(`/api/fixtures/${props.fixtureId}/league`, {
      league_id: league.id
    })
    emit('league-updated', league)
    emit('close')
  } catch (err) {
    console.error('Failed to update league:', err)
    errorMessage.value = err.response?.data?.message || 'Failed to update league'
  } finally {
    saving.value = false
  }
}

function closeModal() {
  if (saving.value) return
  searchQuery.value = ''
  errorMessage.value = ''
  pendingLeague.value = null
  emit('close')
}

watch(() => props.show, (isVisible) => {
  if (isVisible) {
    fetchLeagues()
    searchQuery.value = ''
    errorMessage.value = ''
    pendingLeague.value = null
  }
})
</script>

<template>
  <Transition
    enter-active-class="ease-out duration-300"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="ease-in duration-200"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="show"
      class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm"
      @click.self="closeModal"
    >
      <div
        class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[80vh] flex flex-col"
        @click.stop
      >
        <div class="p-4 border-b border-gray-200">
          <div class="flex items-center justify-between">
            <h3 class="text-lg font-semibold text-gray-900">Change League</h3>
            <button
              type="button"
              class="text-gray-400 hover:text-gray-600 transition-colors"
              @click="closeModal"
            >
              <i class="fas fa-times text-lg"></i>
            </button>
          </div>
          <p v-if="currentLeagueName" class="text-sm text-gray-500 mt-1">
            Current: {{ currentLeagueName }}
          </p>
        </div>

        <div class="p-4 border-b border-gray-100">
          <input
            v-model="searchQuery"
            type="text"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            placeholder="Search leagues..."
          />
        </div>

        <div class="p-2 flex-1 overflow-y-auto min-h-[200px]">
          <div v-if="loading" class="flex items-center justify-center py-12">
            <i class="fas fa-spinner fa-spin text-2xl text-blue-500"></i>
          </div>
          <div v-else-if="errorMessage" class="text-red-600 text-sm py-4 text-center">
            {{ errorMessage }}
          </div>
          <div v-else-if="filteredLeagues.length === 0" class="text-gray-500 text-sm py-8 text-center">
            No leagues found
          </div>
          <ul v-else class="space-y-1">
            <li
              v-for="league in filteredLeagues"
              :key="league.id"
              class="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors group"
              :class="{
                'bg-blue-50': league.id == currentLeagueId,
                'bg-amber-50 ring-1 ring-amber-300': pendingLeague && league.id == pendingLeague.id
              }"
              @click="selectLeague(league)"
            >
              <div class="flex flex-col min-w-0">
                <span class="text-sm font-medium text-gray-900">
                  {{ league.league_name || league.name || 'Unknown' }}
                </span>
                <span v-if="isAdmin && league.slug" class="text-xs text-gray-500 truncate">
                  {{ league.slug }}
                </span>
              </div>
              <span
                v-if="league.id == currentLeagueId"
                class="text-xs text-blue-600 font-medium"
              >
                Current
              </span>
              <i
                v-else
                class="fas fa-chevron-right text-gray-400 text-xs group-hover:text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity"
              ></i>
            </li>
          </ul>
        </div>

        <div v-if="pendingLeague && !saving" class="p-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <p class="text-sm text-gray-800">
            Move fixture #{{ fixtureId }} from
            <span class="font-semibold">{{ currentLeagueName || '—' }}</span>
            to
            <span class="font-semibold">{{ pendingLeague.league_name || pendingLeague.name || 'Unknown' }}</span>?
          </p>
          <p v-if="warning" class="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
            <i class="fas fa-exclamation-triangle mr-1"></i>{{ warning }}
          </p>
          <div class="mt-3 flex justify-end gap-2">
            <button
              type="button"
              class="px-3 py-1.5 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-white"
              @click="pendingLeague = null; errorMessage = ''"
            >
              Cancel
            </button>
            <button
              type="button"
              class="px-3 py-1.5 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700"
              @click="saveLeague(pendingLeague)"
            >
              Change league
            </button>
          </div>
        </div>

        <div v-if="saving" class="p-4 border-t border-gray-100 text-center">
          <i class="fas fa-spinner fa-spin text-blue-500"></i>
          <span class="ml-2 text-sm text-gray-600">Updating...</span>
        </div>
      </div>
    </div>
  </Transition>
</template>
